import Cookies from 'js-cookie';
import { getBaseUrl } from '../../utils/baseUrl';

import type { AllocationResult, AllocateInput, BulkAllocationInput, EditAllocationInput, EditMembershipInput, Grid, Lookup, Me, MembershipPeriodResult, Project, Role, SaveProjectInput, SaveTeamInput, Team } from '../../DTOS/ResourcePlanning';

const endpoint = (path: string) => `/resource-planning${path}`;

const STATUS_MESSAGES: Record<number, string> = { 403: 'Nu aveti drepturi pentru aceasta operatie.', 404: 'Inregistrarea nu exista.' };
const errorText = (body: any, status?: number) => body?.errors?.join(' ') || (status && STATUS_MESSAGES[status]) || 'Operatia nu a putut fi finalizata.';

// Not utils/request: it throws "HTTP error! Status: 403" without reading the body, which hides
// the controller's localized message on 403/404/500 responses.
async function send(path: string, method: string, body?: BodyInit): Promise<Response> {
  const token = Cookies.get('authu');
  const response = await fetch(`${getBaseUrl()}${endpoint(path)}`, {
    method, body, credentials: 'include',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(typeof body === 'string' ? { 'Content-Type': 'application/json; charset=UTF-8' } : {}) },
  });
  if (!response.ok) throw new Error(errorText(await response.json().catch(() => null), response.status));
  return response;
}

async function call<T>(path: string, method = 'GET', payload?: unknown, form?: FormData): Promise<T> {
  const response = await send(path, method, form ?? (payload !== undefined ? JSON.stringify({ request: payload }) : undefined));
  const body = await response.json().catch(() => null);
  if (!body || body.errors?.length) throw new Error(errorText(body));
  return body.response;
}

async function pdfBlob(id: string): Promise<Blob> {
  const response = await send(`/projects/${id}/pdf`, 'GET');
  return new Blob([await response.arrayBuffer()], { type: 'application/pdf' });
}
const ResourcePlanningService = {
  me: () => call<Me>('/me'),
  /** Project technicians and team leads; all=true lists every active user (Access tab, admin only). */
  users: (term = '', all = false) => call<Lookup[]>(`/users?term=${encodeURIComponent(term)}${all ? '&all=true' : ''}`),
  systems: () => call<Lookup[]>('/systems'),
  /** Clients (ERP companies) by name or CUI, at most 50; planners only. */
  companies: (term = '') => call<Lookup[]>(`/companies?term=${encodeURIComponent(term)}`),
  roles: () => call<Role[]>('/roles'),
  assignRole: (userId: string, role: string | null) => call<boolean>('/roles', 'POST', { userId, role }),
  teams: () => call<Team[]>('/teams'),
  saveTeam: (payload: SaveTeamInput) => call<string>('/teams', 'POST', payload),
  move: (payload: { userId: string; teamId?: string | null; effectiveFrom: string }) => call<{ affectedAllocations: number }>('/teams/move', 'POST', payload),
  swap: (payload: { userId: string; otherUserId: string; effectiveFrom: string }) => call<{ affectedAllocations: number }>('/teams/swap', 'POST', payload),
  editMembership: (payload: EditMembershipInput) => call<MembershipPeriodResult>('/teams/membership', 'POST', payload),
  setTeamStart: (teamId: string, validFrom: string | null) => call<MembershipPeriodResult>('/teams/start', 'POST', { teamId, validFrom }),
  projects: () => call<Project[]>('/projects'),
  projectResponsibles: () => call<Lookup[]>('/project-responsibles'),
  saveProject: (payload: SaveProjectInput) => call<string>('/projects', 'POST', payload),
  uploadPdf: (id: string, file: File, expectedVersion: number) => {
    const form = new FormData(); form.append('file', file); form.append('expectedVersion', String(expectedVersion));
    return call<number>(`/projects/${id}/pdf`, 'POST', undefined, form);
  },
  removePdf: (id: string, expectedVersion: number) => call<number>(`/projects/${id}/pdf?expectedVersion=${expectedVersion}`, 'DELETE'),
  pdfBlob,
  downloadPdf: async (project: Project) => {
    const url = URL.createObjectURL(await pdfBlob(project.id));
    const link = document.createElement('a'); link.href = url; link.download = project.pdfName || 'contract.pdf'; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  },
  /** include: people without work or a team in the period who should still appear in the plan. */
  grid: (from: string, to: string, include: string[] = []) =>
    call<Grid>(`/allocations?from=${from}&to=${to}${include.map(id => `&include=${encodeURIComponent(id)}`).join('')}`),
  saveDailyCapacity: (payload: { userId: string; date: string; minutes: number | null; expectedVersion: number }) => call<boolean>('/daily-capacity', 'POST', payload),
  allocate: (payload: AllocateInput) => call<AllocationResult>('/allocations', 'POST', payload),
  editAllocation: (payload: EditAllocationInput) => call<AllocationResult>('/allocations/edit', 'POST', payload),
  deleteAllocation: (id: string, expectedVersion: number) => call<boolean>('/allocations/delete', 'POST', { id, expectedVersion }),
  bulk: (payload: BulkAllocationInput) => call<AllocationResult>('/allocations/bulk', 'POST', payload),
};
export default ResourcePlanningService;
